/**
 * List Score Calculation Helpers
 * Calculates community score for lists based on voting patterns
 */

export interface ListVoteTotals {
  valid: number;
  debatable: number;
  trash: number;
  total: number;
}

export interface ListVotePercentages {
  valid: number;
  debatable: number;
  trash: number;
}

export interface ListScoreSummary {
  listScore: number;
  listScoreLabel: string;
  listVoteTotals: ListVoteTotals;
  listVotePercentages: ListVotePercentages;
}

/**
 * Calculate list score based on community votes
 * @param validCount Number of valid votes
 * @param debatableCount Number of debatable votes
 * @param trashCount Number of trash votes
 * @returns Score from 0-100 representing community respect
 */
export function calculateListScore(
  validCount: number,
  debatableCount: number,
  trashCount: number,
): number {
  const totalVotes = validCount + debatableCount + trashCount;

  // Default to neutral score if no votes
  if (totalVotes === 0) {
    return 50;
  }

  // Step 1: Calculate sentiment (-1 to 1)
  const sentiment = (validCount - trashCount) / totalVotes;

  // Step 2: Normalize to 0-100 scale
  const baseScore = ((sentiment + 1) / 2) * 100;

  // Step 3: Apply confidence weighting toward neutral (50)
  const confidence = Math.min(totalVotes / 20, 1);
  const finalScore = baseScore * confidence + 50 * (1 - confidence);

  // Step 4: Round to integer
  return Math.round(finalScore);
}

/**
 * Get human-readable label for list score
 * @param score List score (0-100)
 * @param totalVotes Total number of votes (optional, for threshold checking)
 * @returns Label describing the score range
 */
export function getListScoreLabel(score: number, totalVotes?: number): string {
  // If we have vote count and it's below threshold, return "Unrated"
  if (totalVotes !== undefined && totalVotes < 5) {
    return "Unrated";
  }

  if (score >= 85) return "Elite";
  if (score >= 70) return "Valid";
  if (score >= 55) return "Debatable";
  return "Trash";
}

/**
 * Calculate vote percentages from totals
 * @param validCount Number of valid votes
 * @param debatableCount Number of debatable votes
 * @param trashCount Number of trash votes
 * @returns Percentage breakdown of votes
 */
export function calculateListVotePercentages(
  validCount: number,
  debatableCount: number,
  trashCount: number,
): ListVotePercentages {
  const total = validCount + debatableCount + trashCount;

  if (total === 0) {
    return {
      valid: 0,
      debatable: 0,
      trash: 0,
    };
  }

  return {
    valid: Math.round((validCount / total) * 100),
    debatable: Math.round((debatableCount / total) * 100),
    trash: Math.round((trashCount / total) * 100),
  };
}

/**
 * Calculate comprehensive list score summary
 * @param validCount Number of valid votes
 * @param debatableCount Number of debatable votes
 * @param trashCount Number of trash votes
 * @returns Complete score summary with totals and percentages
 */
export function calculateListScoreSummary(
  validCount: number,
  debatableCount: number,
  trashCount: number,
): ListScoreSummary {
  const total = validCount + debatableCount + trashCount;
  const listScore = calculateListScore(validCount, debatableCount, trashCount);
  const listScoreLabel = getListScoreLabel(listScore, total);
  const percentages = calculateListVotePercentages(
    validCount,
    debatableCount,
    trashCount,
  );

  return {
    listScore,
    listScoreLabel,
    listVoteTotals: {
      valid: validCount,
      debatable: debatableCount,
      trash: trashCount,
      total,
    },
    listVotePercentages: percentages,
  };
}
